import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  ActivityFileTypeEnum,
  BookingItemResourceInterface,
  OfferInterface,
  TimeslotInterface,
} from '@sport-activities/sdk';
import format from 'date-fns/format';

import { AgeRequirementService } from '~/core/services/activity/age-requirement.service';
import { VtmnPlayButtonSizeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-size.enum';
import { VtmnPlayButtonVariantEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-variant.enum';
import { DividerVariantEnum } from '~/component/divider/enums/divider-variant.enum';
import { LogoTypeEnum } from '~/component/logo/enums/logo-type.enum';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { Activity } from '~/core/model/activity.model';
import { ActivityService } from '~/core/services/activity/activity.service';
import { TitleService } from '~/core/services/title/title.service';
import { LocaleService } from '~/i18n/services/locale.service';
import { ActivityMediaResolutionEnum } from '~/organizer/shared/enums/media-resolution.enum';
import { ParamethorService } from '~/paramethor/paramethor.service';

@Component({
  selector: 'app-timeslot-participant-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
})
export class OrganizerTimeslotPageParticipantListComponent implements OnInit {
  public attendees = [];
  public readonly dividerVariant = DividerVariantEnum;
  public readonly iconType = VtmnPlayIconTypeEnum;
  public readonly vtmnPlayButtonVariant = VtmnPlayButtonVariantEnum;
  public readonly logoType = LogoTypeEnum;
  public readonly majority: number;
  public bookingItems: BookingItemResourceInterface[] = [];
  public timeslot: TimeslotInterface;
  public readonly vtmnPlayButtonSize = VtmnPlayButtonSizeEnum;
  public activity: Activity;
  public readonly dateLocale: Locale;
  public phoneNumberDisplayLimit = 90;

  constructor(
    private readonly ageRequirement: AgeRequirementService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly titleService: TitleService,
    public readonly location: Location,
    public readonly localeService: LocaleService,
    public readonly activityService: ActivityService,
    public readonly paramethorService: ParamethorService,
    public readonly translateService: TranslateService
  ) {
    this.dateLocale = this.localeService.dateLocale;
    this.majority = +this.ageRequirement.tenantMajority;
  }

  public ngOnInit(): void {
    this.titleService.setPageTitle(
      'seo.organizer.timeslot.participant.list.title'
    );

    const { bookingItems, timeslotData } = this.activatedRoute.snapshot.data;
    this.bookingItems = bookingItems;

    bookingItems.filter((booking) => {
      this.sortAttendees(booking.attendees);

      booking.attendees.filter((item) => {
        this.attendees.push(item);
      });
    });

    this.sortAttendees(this.attendees);

    this.timeslot = timeslotData.timeslot;
    this.activityService
      .getActivity(this.timeslot.activityIdentifier)
      .subscribe((activity) => {
        this.activity = activity;
      });
  }

  public get listTitle(): string {
    if (this.totalParticipants === 0) {
      return this.translateService.instant(
        'organizer.participant_list.title.none'
      );
    }

    if (this.totalParticipants === 1) {
      return this.translateService.instant(
        'organizer.participant_list.title.singular'
      );
    }

    return this.translateService.instant(
      'organizer.participant_list.title.plural',
      {
        total_participants: this.totalParticipants,
      }
    );
  }

  public get totalAttendees(): string {
    return this.translateService.instant('organizer.participant.total', {
      totalParticipants: this.totalParticipants,
      maxParticipants: this.timeslot.maximumAttendeeCapacity,
    });
  }

  public get iconName(): VtmnPlayIconTypeEnum {
    return VtmnPlayIconTypeEnum.Fullscreen;
  }

  public get backLink(): string {
    return `/organizer.timeslot.manage/${this.timeslot?.identifier}`;
  }

  public get participantManageLink(): string {
    return `/organizer.timeslot.participant.manage/${this.timeslot?.identifier}`;
  }

  public get totalBookings(): number {
    return this.bookingItems?.length || 0;
  }

  public get totalParticipants(): number {
    return this.bookingItems.reduce(
      (totalParticipants, bookingItem) =>
        totalParticipants + bookingItem?.attendees?.length,
      0
    );
  }

  public displayPhoneNumber(): boolean {
    const today = new Date();
    const timeslotDate = new Date(this.timeslot?.startDate);
    const millisecondsDiff = Math.abs(
      today?.getTime() - timeslotDate?.getTime()
    );
    const millisecondsInDay: number = 1000 * 60 * 60 * 24;
    const daysDiff = Math.floor(millisecondsDiff / millisecondsInDay);

    return (
      Date.parse(today.toDateString()) <
        Date.parse(timeslotDate.toDateString()) ||
      daysDiff < this.phoneNumberDisplayLimit
    );
  }

  public isAdult(age: number): boolean {
    return age > this.majority;
  }

  public getItemPrice(item): number {
    if (item.promotion) {
      // if promo code, need to divide finalPrice by number of attendees
      return item.promotion.finalPrice / item.attendees?.length;
    }
    return item.price;
  }

  public getPhoneNumberTranslation(phoneNumber): string {
    if (!phoneNumber) {
      return this.translateService.instant(
        'organizer.participant_list.booking.no_phone'
      );
    }
    return this.translateService.instant(
      'organizer.participant_list.booking.phone',
      {
        phoneNumber: phoneNumber,
      }
    );
  }

  public getPromoCodeTrans(promoCodeLabel): string {
    return this.translateService.instant(
      'organizer.participant_list.booking.code_promo',
      {
        promoCode: promoCodeLabel,
      }
    );
  }

  public getTotalPrice(item): number {
    if (item.promotion) {
      return item.promotion.finalPrice;
    }
    return item.price * item.attendees?.length;
  }

  public printList() {
    window.print();
  }

  public sortAttendees(bookingToSort) {
    bookingToSort.sort(function (a, b) {
      const attendeeA = a?.name?.toUpperCase();
      const attendeeB = b?.name?.toUpperCase();

      if (attendeeA < attendeeB) {
        return -1;
      }
      return attendeeA > attendeeB ? 1 : 0;
    });
  }

  public getPicture(activity: Activity): string {
    return (
      activity?.getMediaUrlByType(
        'MAIN_MEDIA' as ActivityFileTypeEnum,
        ActivityMediaResolutionEnum.ICON
      ) || activity?.picture
    );
  }

  public formatDate(date: string): string {
    return format(new Date(date), 'P - p', {
      locale: this.localeService.dateLocale,
    });
  }

  public getOfferByIdentifier(offerIdentifier: string): OfferInterface {
    return this.timeslot.offers.find((o) => o?.identifier === offerIdentifier);
  }
}
